import { Fragment } from 'react';
import { connect } from 'react-redux';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { ApplicationState } from '@mews-commander/types';
import { IconName, Tag } from '@optimus-web/core';
import { LocalizedText, useFeatureFlag } from '@mews/framework';
import { getCurrentEnterprise } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { hasCurrentEnterpriseServiceProviderMode } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    isCurrentProfilePlatformAdmin,
    canCurrentProfileViewCustomerData,
    canCurrentProfileManageSubscriptions,
    canCurrentProfileManageCharitySubscriptions,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { isEnterpriseCityTaxReportEnabled } from '@mews-commander/legacy-entities/src/ducks/services';
import { usePrivilege } from '@mews-commander/core';
import { createListItemWithLink } from '../helpers';
import { SidebarExpandableListItem, SidebarFontIcon } from '../style';
import { useNewLedgersSidebarLinkEnabled, useGetNewFinanceLedgersPath } from '../../finance/ledgers/common/hooks';
import { useHasSwedishIntegrationEnabled } from '../../swedishFiscalReport';

const subscriptionsLink = createListItemWithLink({
    textKey: TextKey.EnterpriseSubscriptionPlural,
    iconName: IconName.Gift,
    path: 'EnterpriseSubscription/Index',
});

const FinanceMenu = ({
    hasServiceProviderMode,
    isPlatformAdmin,
    currentEnterprise,
    hasCityTaxReport,
    canManageCharitySubscriptions,
    canManageSubscriptions,
    canViewCustomerData,
    ...other
}: $TSFixMe) => {
    const canViewSensitiveReports = usePrivilege('viewSensitiveReports');
    const showSubscriptions = canManageSubscriptions || canManageCharitySubscriptions;
    const managerReportEnabled = useFeatureFlag(FeatureFlag.managerReportEnabled);
    const newFinanceLedgersPath = useGetNewFinanceLedgersPath(isPlatformAdmin);
    const showNewFinanceLedgers = useNewLedgersSidebarLinkEnabled(isPlatformAdmin);
    const ledgerActivityReportEnabled = useFeatureFlag(FeatureFlag.ledgerActivityReportEnabled);
    const { hasSwedishFiscalIntegration } = useHasSwedishIntegrationEnabled();

    return (
        <SidebarExpandableListItem
            leftIcon={<SidebarFontIcon value={IconName.Finance} />}
            primaryText={<LocalizedText textKey={TextKey.Finance} />}
            {...other}
        >
            {showSubscriptions && subscriptionsLink}
            {isPlatformAdmin && (
                <Fragment>
                    {createListItemWithLink({
                        textKey: TextKey.PlatformTransactionReport,
                        iconName: IconName.ExecuteMerchant,
                        path: 'PlatformTransactionReport/Index',
                    })}
                    {createListItemWithLink({
                        textKey: TextKey.PaymentReport,
                        iconName: IconName.PaymentReport,
                        path: 'PaymentReport/Index',
                    })}
                    {createListItemWithLink({
                        textKey: TextKey.ChargebackReport,
                        iconName: IconName.Chargeback,
                        path: 'ChargebackReport/Index',
                    })}
                    {createListItemWithLink({
                        textKey: TextKey.ConversionRatePlural,
                        iconName: IconName.PaymentReport,
                        path: 'ConversionRate/Index',
                    })}
                    {canViewCustomerData &&
                        createListItemWithLink({
                            textKey: TextKey.FiscalRecordReport,
                            iconName: IconName.FiscalReport,
                            path: 'FiscalRecordReport/Index',
                        })}
                    {createListItemWithLink({
                        textKey: TextKey.MerchantReport,
                        iconName: IconName.PaymentReport,
                        path: 'MerchantReport/Index',
                    })}
                </Fragment>
            )}
            {hasServiceProviderMode && currentEnterprise.id && (
                <Fragment>
                    {createListItemWithLink({
                        textKey: TextKey.Cashier,
                        iconName: IconName.Cashier,
                        path: 'Cashier/Default',
                    })}
                    {canViewSensitiveReports &&
                        showNewFinanceLedgers &&
                        createListItemWithLink({
                            textKey: TextKey.Ledgers,
                            iconName: IconName.AccountingLedger,
                            path: newFinanceLedgersPath,
                            label: (
                                <Tag variant="primary">
                                    <LocalizedText textKey={TextKey.Beta} />
                                </Tag>
                            ),
                        })}
                    {ledgerActivityReportEnabled &&
                        canViewSensitiveReports &&
                        createListItemWithLink({
                            textKey: TextKey.LedgerActivityReport,
                            iconName: IconName.ActivityReport,
                            path: 'LedgerActivityReport/Index',
                            label: (
                                <Tag variant="primary">
                                    <LocalizedText textKey={TextKey.Beta} />
                                </Tag>
                            ),
                        })}
                    {canViewSensitiveReports &&
                        createListItemWithLink({
                            textKey: TextKey.AccountingReport,
                            iconName: IconName.Summary,
                            path: 'AccountingReport/Index',
                        })}
                    {createListItemWithLink({
                        textKey: TextKey.AccountingLedgerReport,
                        iconName: IconName.AccountingLedger,
                        path: 'AccountingLedgerReport/Index',
                    })}
                    {createListItemWithLink({
                        textKey: TextKey.PaymentReport,
                        iconName: IconName.PaymentReport,
                        path: 'PaymentReport/Index',
                    })}

                    {canViewCustomerData &&
                        createListItemWithLink({
                            textKey: TextKey.BillReport,
                            iconName: IconName.ProductReport,
                            path: 'BillReport/Index',
                        })}
                    {canViewCustomerData &&
                        createListItemWithLink({
                            textKey: TextKey.FiscalRecordReport,
                            iconName: IconName.FiscalReport,
                            path: 'FiscalRecordReport/Index',
                        })}
                    {createListItemWithLink({
                        textKey: TextKey.PostingJournalReport,
                        iconName: IconName.PostingJournal,
                        path: 'PostingJournalReport/Index',
                    })}
                    {canViewSensitiveReports &&
                        createListItemWithLink({
                            textKey: TextKey.ManagerReport,
                            iconName: IconName.ManagerReport,
                            path: 'ManagerReport/Index',
                        })}
                    {canViewSensitiveReports &&
                        managerReportEnabled &&
                        createListItemWithLink({
                            textKey: TextKey.ManagerReport,
                            iconName: IconName.ManagerReport,
                            path: 'Finance/ManagerReport/Index/$workspaceId',
                            params: { workspaceId: '' },
                            label: (
                                <Tag variant="primary">
                                    <LocalizedText textKey={TextKey.Beta} />
                                </Tag>
                            ),
                        })}
                    {createListItemWithLink({
                        textKey: TextKey.CreditCardReport,
                        iconName: IconName.PaymentCard,
                        path: 'CreditCardReport/Index',
                    })}
                    {createListItemWithLink({
                        textKey: TextKey.ChargebackReport,
                        iconName: IconName.Chargeback,
                        path: 'ChargebackReport/Index',
                    })}
                    {canViewSensitiveReports &&
                        createListItemWithLink({
                            textKey: TextKey.MerchantBalance,
                            iconName: IconName.PaymentReport,
                            path: 'MerchantBalanceReport/Index',
                        })}
                    {hasCityTaxReport &&
                        createListItemWithLink({
                            textKey: TextKey.CityTaxReport,
                            iconName: IconName.PaymentReport,
                            path: 'CityTaxReport/Index',
                        })}
                    {canViewSensitiveReports &&
                        createListItemWithLink({
                            textKey: TextKey.PayoutReport,
                            iconName: IconName.Chargeback,
                            path: 'PayoutReport/Index',
                        })}
                    {hasSwedishFiscalIntegration &&
                        createListItemWithLink({
                            textKey: TextKey.SwedishCashReport,
                            iconName: IconName.FiscalReport,
                            path: 'SwedishFiscalReport',
                        })}
                </Fragment>
            )}
        </SidebarExpandableListItem>
    );
};

export default connect((state: ApplicationState) => {
    const currentEnterprise = getCurrentEnterprise(state);
    const { id: enterpriseId } = currentEnterprise;

    return {
        isPlatformAdmin: isCurrentProfilePlatformAdmin(),
        hasServiceProviderMode: hasCurrentEnterpriseServiceProviderMode(state),
        hasCityTaxReport: isEnterpriseCityTaxReportEnabled(state, { enterpriseId }),
        canManageSubscriptions: canCurrentProfileManageSubscriptions(state),
        canManageCharitySubscriptions: canCurrentProfileManageCharitySubscriptions(state),
        currentEnterprise,
        canViewCustomerData: canCurrentProfileViewCustomerData(state),
    };
})(FinanceMenu);
